import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, MapPin, Upload, Image } from 'lucide-react';
import { locationsManager } from '../../utils/locationsManager';
import { settingsAPI, uploadAPI } from '../../services/apiService';

const AdminLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    title_de: '',
    title_ru: '',
    description_de: '',
    description_ru: '',
    content_de: '',
    content_ru: '',
    hero_subtitle_de: '',
    hero_subtitle_ru: '',
    hero_image: '',
    contact_text_de: '',
    contact_text_ru: '',
    meta_keywords_de: '',
    meta_keywords_ru: '',
    active: true,
    sort_order: 0
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Try to load from backend first (use public API to avoid auth issues)
      const response = await publicAPI.getLocations();
      const backendLocations = response.data.locations || [];
      
      // Update local storage and locations manager
      locationsManager.saveLocations(backendLocations);
      setLocations(backendLocations);
      
      // Clear error on successful load
      setError('');
      
    } catch (error) {
      console.warn('Could not load from server, using local data:', error);
      
      // Try to load from localStorage without showing error
      const localLocations = locationsManager.getAllLocations();
      if (localLocations && localLocations.length > 0) {
        setLocations(localLocations);
        setError(''); // Don't show error if we have local data
      } else {
        // Only show error if we have no data at all
        setError('Нет доступных локаций');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = () => {
    setEditingLocation(null);
    setError(''); // Clear any errors when creating new location
    setFormData({
      name: '',
      slug: '',
      title_de: '',
      title_ru: '',
      description_de: '',
      description_ru: '',
      content_de: '',
      content_ru: '',
      hero_subtitle_de: '',
      hero_subtitle_ru: '',
      hero_image: '',
      contact_text_de: '',
      contact_text_ru: '',
      meta_keywords_de: '',
      meta_keywords_ru: '',
      active: true,
      sort_order: locations.length + 1
    });
    setShowForm(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setError(''); // Clear any errors when editing location
    setFormData({ ...location });
    setShowForm(true);
  };

  const handleDeleteLocation = async (locationId) => {
    if (window.confirm('Удалить эту локацию?')) {
      try {
        setError('');
        await settingsAPI.deleteLocation(locationId);
        
        // Update local state and localStorage
        const updatedLocations = locations.filter(loc => loc.id !== locationId);
        setLocations(updatedLocations);
        locationsManager.saveLocations(updatedLocations);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('locations-updated'));
        
        alert('Локация удалена!');
      } catch (error) {
        console.error('Error deleting location:', error);
        setError('Failed to delete location');
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      const locationData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name)
      };

      console.log('Saving location data:', locationData);
      console.log('Hero image in data:', locationData.hero_image);

      let updatedLocations;
      if (editingLocation) {
        // Update existing location
        console.log('Updating location with ID:', editingLocation.id);
        await settingsAPI.updateLocation(editingLocation.id, locationData);
        updatedLocations = locations.map(loc => 
          loc.id === editingLocation.id ? { ...locationData, id: editingLocation.id } : loc
        );
      } else {
        // Create new location
        console.log('Creating new location');
        const response = await settingsAPI.createLocation(locationData);
        const newLocation = response.data.location;
        console.log('Created location:', newLocation);
        updatedLocations = [...locations, newLocation];
      }

      // Update local state and localStorage
      setLocations(updatedLocations);
      locationsManager.saveLocations(updatedLocations);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('locations-updated'));
      
      setShowForm(false);
      setEditingLocation(null); // Clear editing state
      alert('Локация сохранена!');
    } catch (error) {
      console.error('Error saving location:', error);
      setError('Failed to save location');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    try {
      setUploading(true);
      setError('');
      
      console.log('Uploading image file:', file.name);
      // Pass file directly to uploadAPI.uploadImage - it will create FormData internally
      const response = await uploadAPI.uploadImage(file);
      console.log('Upload response:', response.data);
      
      const imageUrl = response.data.url;
      
      if (imageUrl) {
        console.log('Setting hero_image to:', imageUrl);
        setFormData(prev => ({
          ...prev,
          hero_image: imageUrl
        }));
        
        // Clear any previous errors
        setError('');
        alert('Изображение загружено успешно! Теперь можете сохранить локацию.');
      } else {
        throw new Error('No URL in upload response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Ошибка загрузки изображения: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    setFormData(prev => ({
      ...prev,
      hero_image: ''
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление локациями</h1>
          <p className="text-gray-300">SEO настройки и контент для страниц городов</p>
        </div>
        <button
          onClick={handleCreateLocation}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          Добавить локацию
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-md">
          <p>{error}</p>
        </div>
      )}

      {/* Locations List */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg">
        <div className="p-6">
          <div className="space-y-4">
            {locations.map((location) => (
              <div key={location.id} className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-blue-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">{location.name}</h3>
                      <p className="text-sm text-gray-400">/{location.slug}</p>
                      <p className="text-sm text-gray-300 mt-1">{location.title_de}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!location.active && (
                      <span className="px-2 py-1 bg-red-900 text-red-300 text-xs rounded">Неактивно</span>
                    )}
                    <button
                      onClick={() => handleEditLocation(location)}
                      className="text-blue-400 hover:text-blue-300 p-1"
                      title="Редактировать"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteLocation(location.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingLocation ? 'Редактировать локацию' : 'Новая локация'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Название города
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      URL Slug
                    </label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="автоматически из названия"
                    />
                  </div>
                </div>

                {/* SEO Titles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇩🇪 SEO Заголовок (немецкий)
                    </label>
                    <input
                      type="text"
                      name="title_de"
                      value={formData.title_de}
                      onChange={handleInputChange}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇷🇺 SEO Заголовок (русский)
                    </label>
                    <input
                      type="text"
                      name="title_ru"
                      value={formData.title_ru}
                      onChange={handleInputChange}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                {/* Meta Descriptions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇩🇪 Meta описание (немецкий)
                    </label>
                    <textarea
                      name="description_de"
                      value={formData.description_de}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇷🇺 Meta описание (русский)
                    </label>
                    <textarea
                      name="description_ru"
                      value={formData.description_ru}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Hero Subtitle */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇩🇪 Hero подзаголовок (немецкий)
                    </label>
                    <textarea
                      name="hero_subtitle_de"
                      value={formData.hero_subtitle_de}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Подзаголовок в Hero секции"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇷🇺 Hero подзаголовок (русский)
                    </label>
                    <textarea
                      name="hero_subtitle_ru"
                      value={formData.hero_subtitle_ru}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Подзаголовок в Hero секции"
                    />
                  </div>
                </div>

                {/* Hero Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    🖼️ Hero фоновое изображение
                  </label>
                  
                  {/* Image Preview */}
                  {formData.hero_image && (
                    <div className="mb-4 relative">
                      <img 
                        src={formData.hero_image} 
                        alt="Hero preview" 
                        className="w-full h-32 object-cover rounded-md border border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={handleImageRemove}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex items-center space-x-3 mb-3">
                    <label className={`flex items-center px-4 py-2 rounded-md cursor-pointer transition-colors ${
                      formData.hero_image 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Загрузка...' : 
                       formData.hero_image ? 'Изменить изображение' : 'Загрузить изображение'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => handleImageUpload(e.target.files[0])}
                      />
                    </label>
                    {formData.hero_image && (
                      <span className="text-green-400 text-sm">✅ Загружено</span>
                    )}
                  </div>
                  
                  {/* URL Input - Made less strict */}
                  <input
                    type="text"
                    name="hero_image"
                    value={formData.hero_image}
                    onChange={handleInputChange}
                    className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="URL изображения или используйте кнопку загрузки выше"
                    readOnly={uploading}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.hero_image ? 
                      '✅ Изображение установлено. Можете сохранить локацию.' : 
                      'URL изображения для фона Hero секции или загрузите файл выше'
                    }
                  </p>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇩🇪 Основной контент (немецкий)
                    </label>
                    <textarea
                      name="content_de"
                      value={formData.content_de}
                      onChange={handleInputChange}
                      rows={5}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Дополнительный контент для страницы города"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇷🇺 Основной контент (русский)
                    </label>
                    <textarea
                      name="content_ru"
                      value={formData.content_ru}
                      onChange={handleInputChange}
                      rows={5}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Дополнительный контент для страницы города"
                    />
                  </div>
                </div>

                {/* Contact Text */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇩🇪 Контактный текст (немецкий)
                    </label>
                    <textarea
                      name="contact_text_de"
                      value={formData.contact_text_de}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Текст в контактной секции"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇷🇺 Контактный текст (русский)
                    </label>
                    <textarea
                      name="contact_text_ru"
                      value={formData.contact_text_ru}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Текст в контактной секции"
                    />
                  </div>
                </div>

                {/* Keywords */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇩🇪 Ключевые слова (немецкий)
                    </label>
                    <input
                      type="text"
                      name="meta_keywords_de"
                      value={formData.meta_keywords_de}
                      onChange={handleInputChange}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="через запятую"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🇷🇺 Ключевые слова (русский)
                    </label>
                    <input
                      type="text"
                      name="meta_keywords_ru"
                      value={formData.meta_keywords_ru}
                      onChange={handleInputChange}
                      className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="через запятую"
                    />
                  </div>
                </div>

                {/* Active checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 border-gray-600 bg-gray-700 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="active" className="ml-2 text-sm text-gray-300">
                    Активная локация
                  </label>
                </div>

                {/* Submit */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingLocation ? 'Обновить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLocations;